import React, { useEffect, useReducer, createContext, useState, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuid } from 'uuid'
import { useQueryParams } from '@fs/zion-router'
import useDeepEqualMemoization from '../../../../shared/hooks/useDeepEqualMemoization'

export const GroupDataContext = createContext()
export const GroupDataUpdateContext = createContext(() => {})

const INITIAL_GROUP_DATA_STATE = {
  auditFeedback: [],
  coverageErrors: {},
  group: {},
  groupCoverageCheckboxes: [],
  currentCoverages: [],
  initialGroup: {},
  initialGroupWorkflowActions: [],
  initialGroupCoverages: [],
  itemCount: 1,
  itemNumber: 1,
  updating: false,
  volumeSetCoverageCheckboxes: [],
}

function groupDataReducer(state, action) {
  switch (action.type) {
    case 'HANDLE_NEW_SERVER_DATA': {
      action.value.setLoading(false)
      return getInitializationData(action.value.data, action.value.initialChildIndex)
    }
    case 'RESET': {
      return {
        ...state,
        group: { ...getOriginalGroup(state.initialGroup, action), coverages: [...(state.currentCoverages || [])] },
      }
    }
    case 'SET_COVERAGES':
      return action.forVolumeSet
        ? {
            ...state,
            initialGroup: { ...state.initialGroup, coverages: action.value },
            volumeSetCoverageCheckboxes: getCoverageCheckboxes(action.value, false),
          }
        : {
            ...state,
            group: { ...state.group, coverages: action.value },
            groupCoverageCheckboxes: getCoverageCheckboxes(action.value, false),
          }
    case 'SET_COVERAGE_CHECKBOXES': {
      const propertyName = action.forVolumeSet ? 'volumeSetCoverageCheckboxes' : 'groupCoverageCheckboxes'
      return { ...state, [propertyName]: action.value }
    }
    case 'UPDATE_ALL_COVERAGE_CHECKBOXES': {
      const propertyName = action.forVolumeSet ? 'volumeSetCoverageCheckboxes' : 'groupCoverageCheckboxes'
      const coveragesValue = action.forVolumeSet ? state.initialGroup.coverages : state.group.coverages
      return { ...state, [propertyName]: getCoverageCheckboxes(coveragesValue, action.value) }
    }
    case 'SET_EDITED': {
      const newState = { ...state, edited: action.value }
      return newState
    }
    case 'SET_GROUP_TO_CHILD': {
      const newGroup = state?.initialGroup?.childGroups?.[action.value]
      const extractedData = extractGroupDataFromServerData(newGroup)

      if (newGroup) {
        return {
          ...state,
          currentCoverages: extractedData.coverages,
          group: {
            id: newGroup.id,
            ...extractedData,
          },
          groupCoverageCheckboxes: getCoverageCheckboxes(newGroup?.imageViewerCoverages || [], false),
          itemCount: state.initialGroup.childGroups.length,
          itemNumber: action.value + 1,
        }
      }

      return {
        ...state,
        itemNumber: action.value + 1,
        groupCoverageCheckboxes: getCoverageCheckboxes(state?.group?.coverages || [], false),
      }
    }
    case 'SET_INTERNAL_FIELDS':
      return { ...state, group: { ...state.group, internalFields: action.value } }
    case 'SET_METADATA':
      return { ...state, group: { ...state.group, metadata: action.value } }
    case 'UPDATE_GROUP_DATA': {
      const newGroup = action.value.group
      const data = extractGroupDataFromServerData(newGroup)
      return {
        ...state,
        group: { ...state.initialData, ...data, id: newGroup.id },
        initialGroup: { ...state.initialData, ...data, id: newGroup.id },
        updating: true,
      }
    }
    case 'UPDATE_INITIAL_COVERAGES':
      return { ...state, initialGroupCoverages: [...state.initialGroup.coverages] }
    default:
      throw new Error(`Unhandled type in groupDataReducer: ${action.type}`)
  }
}

export default function GroupDataProvider({ children, initialData }) {
  const [loading, setLoading] = useState(true)
  const [childGroupIndex, setChildGroupIndex] = useState(0)

  // Redundantly setting query params triggers a new object reference for the query object.
  // Memoizing it stops a lot of unnecessary re-renders as well as some infinite loops.
  const { query: unmemoizedQuery } = useQueryParams()
  const query = useDeepEqualMemoization(unmemoizedQuery)

  const view = query?.view
  const mode = query?.mode
  const initialChildId = query?.groupId
  const [state, dispatch] = useReducer(groupDataReducer, INITIAL_GROUP_DATA_STATE)
  const initialDataSet = useRef(false) // Makes sure that the useEffect below only runs one time after initialData is loaded, so the selected item doesn't reset to 0 on updating metadata

  const initialChildIndex = useMemo(() => {
    if (initialChildId && initialData?.group) {
      const childIndex = initialData?.group?.childGroups
        ?.filter(Boolean)
        ?.findIndex((child) => child.id === initialChildId)
      if (childIndex < 0) {
        return 0
      }
      return childIndex
    }
    return null
  }, [initialChildId, initialData])

  // to set the initial value for childGroupIndex
  useEffect(() => {
    let isMounted = true
    if (typeof initialChildIndex === 'number' && initialChildIndex > -1 && !initialDataSet.current && isMounted) {
      setChildGroupIndex(initialChildIndex)
      initialDataSet.current = true
    }

    return () => {
      isMounted = false
    }
  }, [initialChildId, initialData, initialChildIndex])

  // will run when initialData or query.groupId changes
  useEffect(() => {
    if (
      (initialDataSet.current && typeof initialChildIndex === 'number' && initialChildIndex > -1) ||
      ['index', 'fullText'].includes(view)
    ) {
      dispatch({ type: 'HANDLE_NEW_SERVER_DATA', value: { data: initialData, initialChildIndex, setLoading } })
    }
  }, [initialData, initialChildIndex, view])

  const setGroupIndex = useCallback(
    (newIndex) => {
      setChildGroupIndex(newIndex)
      const isParentGroup = Boolean(initialData?.group?.childGroups?.length)
      // currently the only time that the query.groupId doesn't update when groupIndex is changed is while in divide mode
      // for this case we need to update the data here
      if (
        isParentGroup &&
        (query.volumeSetSearch || (newIndex !== state.group.id && view === 'organize' && mode === 'DIVIDE'))
      ) {
        dispatch({ type: 'SET_GROUP_TO_CHILD', value: newIndex })
      }
    },
    [initialData, mode, query.volumeSetSearch, state.group.id, view]
  )

  const groupDataContextValue = useMemo(
    () => ({
      initialChildId,
      groupIndex: childGroupIndex,
      setGroupIndex,
      loading,
      ...state,
    }),
    [initialChildId, childGroupIndex, setGroupIndex, loading, state]
  )

  return (
    <GroupDataUpdateContext.Provider value={dispatch}>
      <GroupDataContext.Provider value={groupDataContextValue}>{children}</GroupDataContext.Provider>
    </GroupDataUpdateContext.Provider>
  )
}

GroupDataProvider.propTypes = {
  initialData: PropTypes.shape({
    recordTypes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  }),
}

function getInitializationData(serverData, groupIndex = 0) {
  const isParentGroup = Boolean(serverData?.group?.childGroups?.length)
  const currentGroup = isParentGroup ? serverData.group.childGroups[groupIndex] : serverData?.group
  const coverages = currentGroup?.imageViewerCoverages

  const initialGroup = serverData ? extractGroupDataFromServerData(serverData.group) : {}
  let itemCount = isParentGroup ? serverData.group.childGroups.length : currentGroup?.siblingIdsWithChildren?.length
  // if the group is not a parent or have any siblings, it must be by itself having a count of 1; we also don't want to return an undefined value
  // Kira Peters 1/11/24 Lightyear
  if (!itemCount) {
    itemCount = 1
  }
  let itemNumber = isParentGroup ? groupIndex + 1 : currentGroup?.childNumber
  // if child number is 0, then the group must be by itself and must be item 1; we also don't want to return an undefined value
  // Kira Peters 1/11/24
  if (!itemNumber) {
    itemNumber = 1
  }

  let groupToExtract = { ...currentGroup }

  if (currentGroup?.metadata?.volumes) groupToExtract = { ...currentGroup, volumes: currentGroup.metadata.volumes }
  else if (initialGroup?.metadata?.volumes && !groupToExtract.volumes)
    groupToExtract.volumes = initialGroup.metadata.volumes
  else if (!initialGroup?.metadata?.volumes && !groupToExtract.volumes) groupToExtract.volumes = []

  return {
    allRecordTypes: serverData?.recordTypes,
    auditFeedback: getAuditFeedback(serverData?.group?.properties),
    coverageErrors: {},
    group: currentGroup ? extractGroupDataFromServerData(groupToExtract) : {},
    groupCoverageCheckboxes: getCoverageCheckboxes(coverages, false),
    currentCoverages: coverages?.length > 0 ? coverages : [],
    initialGroup,
    initialGroupWorkflowActions: Array.isArray(initialGroup.currentWorkflowActions)
      ? [...initialGroup.currentWorkflowActions]
      : [], // This stores a copy of the original workflowActions and is used to check if group was loaded with reviewCAI workflow action in order to show Record Metadata Review box
    initialGroupCoverages: Array.isArray(initialGroup.coverages) ? [...initialGroup.coverages] : [], // This stores a copy of the original coverages and is used to reset coverages
    itemCount,
    itemNumber,
    updating: false,
    volumeSetCoverageCheckboxes: getCoverageCheckboxes(
      initialGroup.types?.includes('VOLUME_SET') ? initialGroup.coverages : [],
      false
    ),
  }
}

/**
 * @param {*[]} coverages
 * @param {boolean} checked
 * @returns {[]} - An array of an array of objects where each object has:
 *     - recordTypeChecked: boolean,
 *     - datesChecked: boolean[].
 */
function getCoverageCheckboxes(coverages, checked) {
  const coverageCheckboxes = []

  if (coverages) {
    coverages.forEach(({ recordTypes }) => {
      const recordTypesChecked = recordTypes.map((recordType) => {
        const recordCheckBox = { recordTypeChecked: checked, datesChecked: [] }
        recordType.date.forEach(() => {
          recordCheckBox.datesChecked.push(checked)
        })
        return recordCheckBox
      })
      coverageCheckboxes.push(recordTypesChecked)
    })
  }
  return coverageCheckboxes
}

function getAuditFeedback(groupProperties) {
  if (!groupProperties) return []

  const feedbackPropertyValues = groupProperties.find((prop) => prop?.name === 'auditFeedback')?.values
  if (!feedbackPropertyValues) return []

  const onlyFeedbacks = feedbackPropertyValues.filter((value) => value.includes(':ADDITIONAL_FEEDBACK:'))
  const feedbackComments = onlyFeedbacks
    .map((feedbackString) => {
      const metadataTypes = ['DATES', 'RECORD_TYPES', 'PLACES']
      // the underscore is just "ADDITIONAL_FEEDBACK" which we don't need
      // eslint-disable-next-line no-unused-vars
      const [metadataType, _, idx, message, ...otherMatches] = feedbackString.split(':')
      if (!metadataTypes.includes(metadataType) || Number.isNaN(parseInt(idx, 10))) return null

      const joinedOtherMatches = otherMatches?.length > 0 ? otherMatches.join(':') : ''

      const issueTypeValue = feedbackPropertyValues.find((value) => value.includes(`ISSUE:${idx}:`))
      const issueTypeSplit = issueTypeValue.split(':')

      return {
        metadataType,
        feedbackMessage: joinedOtherMatches.length > 0 ? `${message}: ${joinedOtherMatches}` : message,
        issueType: issueTypeSplit[issueTypeSplit.length - 1],
        id: parseInt(idx, 10),
      }
    })
    .filter(Boolean)

  return feedbackComments
}

function extractGroupDataFromServerData(group) {
  const imageViewerCoverages = group?.imageViewerCoverages
  let groupToExtract = { ...group }

  if (group?.metadata?.volumes) groupToExtract = { ...group, volumes: group.metadata.volumes }

  // we need to create unique identifiers for each of the creators so that react has unchanging keys to help it render
  if (group?.metadata?.creator) {
    const creatorsWithIdentifiers = giveUniqueIdentifiersToCreators(groupToExtract.metadata.creator)
    groupToExtract = { ...groupToExtract, metadata: { ...groupToExtract.metadata, creator: creatorsWithIdentifiers } }
  }

  const coverages = [...(Array.isArray(imageViewerCoverages) ? imageViewerCoverages : [])]

  return {
    ...group,
    coverages,
    placeInformation: group?.placeInformation,
    metadata: groupToExtract.metadata
      ? { ...groupToExtract.metadata, volumes: groupToExtract.volumes ? groupToExtract.volumes : [] }
      : null,
    internalFields: {
      internalData: group?.internalData,
      apid: group?.imageApids?.[0],
      properties: group?.properties,
    },
  }
}

function giveUniqueIdentifiersToCreators(creator) {
  const creatorsWithIdentifiers = {}
  if (creator) {
    creator.forEach((c) => {
      const id = uuid()
      creatorsWithIdentifiers[id] = c
    })
  }
  return creatorsWithIdentifiers
}

function getOriginalGroup(initialGroup, action) {
  let originalGroup = { ...initialGroup }

  const isParentGroup = Boolean(initialGroup.childGroups?.length)
  if (isParentGroup) {
    originalGroup = { ...initialGroup.childGroups.find((group) => group.id === action.value) }
  }

  return originalGroup
}
